import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { calculateUserCost } from '../utils/pricing';
import { calculateSegmentArrivalTime, getTravelDuration } from '../utils/timeWindows';
import { dbHelpers, supabase } from '../config/supabase';
import StripePaymentModal from './StripePaymentModal';
import ExplainerModal from './ExplainerModal';
import FallbackPromptModal from './FallbackPromptModal';
import { showFallbackNotification } from '../utils/notifications';
import { sendTripNotification } from '../services/tripNotifyApi';
import { getAvailableRoutesForTrip, segmentsOverlap } from '../utils/routeAnalysis';
import { getDisplayName } from '../utils/profileUtils';
import RouteSharingDisplay from './RouteSharingDisplay';
import SharedTransferPointPicker from './SharedTransferPointPicker';
import { getDefaultPoint } from '../config/sharedTransferLocations';
import { validateSharedTransferLocation } from '../services/sharedTransferLocationApi';
import { validatePaymentEligibility } from '../utils/paymentValidation';
import {
  CONFIRMED_JOIN_CUTOFF_HOURS,
  evaluateTripJoinability,
  extractJoinRejectionReason,
  isManualResolutionConfirmResult,
  joinRejectionMessage,
  manualResolutionMessage,
} from '../config/sharedTransferRules';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, ArrowRight, ArrowLeft, Users, MapPin, Clock, CreditCard, Info, CheckCircle } from 'lucide-react';
import Badge from './ui/Badge';
import CustomPhoneInput from './PhoneInput';
import { normalizePhoneInput } from '../utils/phoneValue';

const stepVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 }
};

const JoinTripModal = ({ isOpen, onClose, onJoin, trip, initialRouteValue = null }) => {
  const { user } = useAuth();
  const [groupSize, setGroupSize] = useState(1);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userCost, setUserCost] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [immediateCharge, setImmediateCharge] = useState(false);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [participantId, setParticipantId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  // Track payment completion via ref to avoid unload race; no state needed
  const participantIdRef = useRef(null);
  const paymentCompletedRef = useRef(false);
  const authTokenRef = useRef(null);
  const [userProfile, setUserProfile] = useState(null);
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [pickupPoint, setPickupPoint] = useState(null);
  const [dropoffPoint, setDropoffPoint] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [showExplainer, setShowExplainer] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);
  
  // Animation states
  const [showSuccessFeedback, setShowSuccessFeedback] = useState(false);
  const [showErrorFeedback, setShowErrorFeedback] = useState(false);

  // Step navigation state
  const [modalStep, setModalStep] = useState(1);

  // Reset modal step when opened
  useEffect(() => {
    if (isOpen) setModalStep(1);
  }, [isOpen]);

  // Sync payment modal to step 3
  useEffect(() => {
    if (showPaymentModal) setModalStep(3);
  }, [showPaymentModal]);

  // Load user profile when modal opens
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!isOpen) return;
      // GUARD: Ensure user and user.user_metadata are defined
      if (!user || !user.user_metadata) {
        console.error('User or user.user_metadata is undefined:', user?.id);
        setUserProfile({
          name: user?.user_metadata?.name || 'User',
          phone: user?.user_metadata?.phone || '',
          pickup_location: '',
          dropoff_location: ''
        });
        return;
      }
      try {
        // Validate supabase before using it
        if (!supabase) {
          // Fallback to user metadata
          setUserProfile({
            name: user?.user_metadata?.name || 'User',
            phone: user?.user_metadata?.phone || '',
            pickup_location: '',
            dropoff_location: ''
          });
          return;
        }
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (!error && profile) {
          setUserProfile({
            name: profile.name || user?.user_metadata?.name || 'User',
            phone: profile.phone || user?.user_metadata?.phone || '',
            pickup_location: '',
            dropoff_location: ''
          });
          setContactName(profile.name || user?.user_metadata?.name || 'User');
          const phoneWithPrefix = normalizePhoneInput(
            profile.phone || user?.user_metadata?.phone || '',
          );
          setContactPhone(phoneWithPrefix);
        } else {
          setUserProfile({
            name: user?.user_metadata?.name || 'User',
            phone: user?.user_metadata?.phone || '',
            order_notes: ''
          });
          setContactName(user?.user_metadata?.name || 'User');
          setContactPhone(normalizePhoneInput(user?.user_metadata?.phone || ''));
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        // Fallback to user metadata
        setUserProfile({
          name: user?.user_metadata?.name || 'User',
          phone: user?.user_metadata?.phone || '',
          order_notes: ''
        });
        setContactName(user?.user_metadata?.name || 'User');
        setContactPhone(normalizePhoneInput(user?.user_metadata?.phone || ''));
      }
    };

    loadUserProfile();
  }, [isOpen, user]);

  // Fetch available joinable routes when modal opens or trip changes
  useEffect(() => {
    const fetchAvailableRoutes = async () => {
      if (!isOpen || !trip) return;
      setRouteError('');
      setAvailableRoutes([]);
      setSelectedRoute(null);
      
      // Get participants first
      const { data: participantList } = await dbHelpers.getTripParticipants(trip.id);
      const participants = participantList || [];
      
      // Add isCreator flag to participants
      const participantsWithCreatorFlag = participants.map(p => ({
        ...p,
        isCreator: p.user_id === trip.creator_id
      }));
      
      // Use the route analysis function to get available routes
      const availableRoutes = getAvailableRoutesForTrip(trip, participantsWithCreatorFlag);
      
      // Format routes for dropdown (add value and label properties)
      const formattedRoutes = availableRoutes.map(route => ({
        ...route,
        value: `${route.pickup}To${route.dropoff}`,
        label: `${route.pickup} → ${route.dropoff}`
      }));
      
      setAvailableRoutes(formattedRoutes);
      if (formattedRoutes.length > 0) {
        const match = initialRouteValue
          ? formattedRoutes.find((route) => route.value === initialRouteValue)
          : null;
        setSelectedRoute(match ?? formattedRoutes[0]);
      } else {
        setRouteError('No joinable routes are currently available for this trip.');
      }
    };
    fetchAvailableRoutes();
  }, [isOpen, trip, initialRouteValue]);

  // Fetch participants when modal opens or trip changes
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!isOpen || !trip) return;
      const { data: participantList } = await dbHelpers.getTripParticipants(trip.id);
      setParticipants(participantList || []);
    };
    fetchParticipants();
  }, [isOpen, trip]);

  // Reset point pickers when segment route changes
  useEffect(() => {
    if (!selectedRoute?.pickup || !selectedRoute?.dropoff) {
      setPickupPoint(null);
      setDropoffPoint(null);
      return;
    }
    setPickupPoint(getDefaultPoint(selectedRoute.pickup));
    setDropoffPoint(getDefaultPoint(selectedRoute.dropoff));
  }, [selectedRoute?.value, selectedRoute?.pickup, selectedRoute?.dropoff]);
  useEffect(() => {
    if (!selectedRoute || !trip || !participants.length) {
      setSharedWith([]);
      return;
    }
    
    const joinerRoute = {
      pickup: selectedRoute.pickup,
      dropoff: selectedRoute.dropoff,
      direction: trip.direction
    };
    
    // Add isCreator flag to participants for shared segments calculation
    const participantsWithCreatorFlag = participants.map(p => ({
      ...p,
      isCreator: p.user_id === trip.creator_id
    }));
    
    const shared = participantsWithCreatorFlag
      .map(p => {
        const joinerSegment = `${joinerRoute.pickup}To${joinerRoute.dropoff}`;
        const participantSegment = p.segment;
        
        const segmentsMatch = segmentsOverlap(joinerSegment, participantSegment, trip.direction);
        
        // Only show participants who actually share segments
        if (segmentsMatch) {
          // Determine which segment this participant actually shares
          let sharedSegmentDisplay;
          if (joinerSegment === participantSegment) {
            // Exact match - show the full segment
            sharedSegmentDisplay = `${joinerRoute.pickup} → ${joinerRoute.dropoff}`;
          } else {
            // Partial match - show the participant's actual segment
            const [participantPickup, participantDropoff] = participantSegment.split('To');
            sharedSegmentDisplay = `${participantPickup} → ${participantDropoff}`;
          }
          
          return { 
            name: getDisplayName(p), 
            segments: [{ from: joinerRoute.pickup, to: joinerRoute.dropoff }], 
            sharedSegmentDisplay,
            isCreator: p.isCreator,
            hasSharedStops: false
          };
        }
        
        return null;
      })
      .filter(Boolean);
    
    setSharedWith(shared);
  }, [selectedRoute, trip, participants]);

  // Calculate cost when route or group size changes
  useEffect(() => {
    const calculateCost = async () => {
      if (!selectedRoute || !trip) {
        setUserCost(0);
        return;
      }
      setIsCalculating(true);
      try {
        const cost = await calculateUserCost(trip, `${selectedRoute.pickup}To${selectedRoute.dropoff}`, groupSize, dbHelpers);
        setUserCost(cost);
      } catch (costError) {
        setUserCost(0);
      } finally {
        setIsCalculating(false);
      }
    };
    calculateCost();
  }, [selectedRoute, groupSize, trip]);

  if (!trip) return null;
  
  const availableSeats = 6 - (trip.seats_filled || 0);

  // Calculate estimated arrival time for the selected segment
  const getSegmentArrivalTime = () => {
    if (!selectedRoute || !trip.time_window_start) return null;
    return calculateSegmentArrivalTime(trip.time_window_start, `${selectedRoute.pickup}To${selectedRoute.dropoff}`);
  };

  const segmentArrivalTime = getSegmentArrivalTime();
  const segmentDuration = selectedRoute ? getTravelDuration(`${selectedRoute.pickup}To${selectedRoute.dropoff}`) : null;

  // Check if trip meets minimum seat requirement
  const isFallbackPending = trip.status === 'fallback_pending';
  
  // Check if joining confirmed trip is allowed
  const canJoinConfirmedTrip = () => {
    if (trip.status !== 'confirmed') return true;
    if (trip.seats_filled >= (trip.max_seats || 6)) return false;
    const joinable = evaluateTripJoinability(trip);
    return joinable.ok;
  };
  
  const isConfirmedTripJoinAllowed = canJoinConfirmedTrip();

  // ── Step navigation handlers ──────────────────────────────────────

  const handleStep1Continue = () => {
    setRouteError('');
    if (!selectedRoute) {
      setRouteError('Please select your route.');
      return;
    }
    if (!contactName?.trim()) {
      setRouteError('Please provide your full name.');
      return;
    }
    if (!phoneValid || !contactPhone?.trim()) {
      setRouteError('Please enter a valid phone number.');
      return;
    }
    if (!pickupPoint || !dropoffPoint) {
      setRouteError('Please confirm your pickup and drop-off points within each city.');
      return;
    }
    setModalStep(2);
  };

  const handleStep2Continue = () => {
    try {
      localStorage.setItem('explainer_seen_join', '1');
    } catch {}
    handleSubmit({ preventDefault: () => {} });
  };

  const handleBack = () => {
    setRouteError('');
    setModalStep(prev => Math.max(1, prev - 1));
  };

  // ── Existing handlers (preserved exactly) ─────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !userProfile) {
      alert('User session or profile is missing. Please refresh and try again.');
      return;
    }
    if (!selectedRoute) {
      alert('Please select your route');
      return;
    }
    
    setIsSubmitting(true);
    setRouteError('');
    setShowErrorFeedback(false);
    setShowSuccessFeedback(false);
    
    try {
      // 🛡️ VALIDATE PAYMENT ELIGIBILITY FIRST
      const eligibility = await validatePaymentEligibility(trip.id, user.email);
      
      if (!eligibility.canPay) {
        console.error('Payment not allowed:', eligibility.reason);
        setRouteError(eligibility.message);
        setShowErrorFeedback(true);
        setIsSubmitting(false);
        return; // Stop here - don't proceed with join
      }
      
      // Validate route before joining
      const joinerRoute = {
        pickup: selectedRoute.pickup,
        dropoff: selectedRoute.dropoff,
        direction: trip.direction
      };
      const { canJoin, error: joinValidationError } = await dbHelpers.validateRouteForJoining(trip.id, joinerRoute);
      if (!canJoin) {
        setRouteError(joinValidationError || 'You cannot join this trip with the selected route.');
        setIsSubmitting(false);
        return;
      }


      // Use the proper joinTripRequest function
      if (!trip) {
        console.error('Trip is undefined in handleSubmit');
        alert('Trip data is missing. Please refresh and try again.');
        setIsSubmitting(false);
        return;
      }
      
      // Ensure contact info present
      if (!contactName?.trim() || !phoneValid || !contactPhone?.trim()) {
        setRouteError('Please provide your name and a valid mobile phone number.');
        setIsSubmitting(false);
        return;
      }

      // Persist updated profile if changed
      try {
        await supabase
          .from('profiles')
          .upsert([{ id: user.id, name: contactName.trim(), phone: contactPhone.trim() }]);
      } catch (e) {
        console.warn('Profile upsert failed (non-blocking):', e);
      }

      // Pre-fetch auth token for potential cleanup on abrupt close
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        authTokenRef.current = sessionData?.session?.access_token || null;
      } catch (_e) {
        authTokenRef.current = null;
      }

      let validatedPickup = pickupPoint;
      let validatedDropoff = dropoffPoint;
      try {
        const pickupResult = await validateSharedTransferLocation(
          { hubCity: selectedRoute.pickup, point: pickupPoint },
          authTokenRef.current,
        );
        validatedPickup = pickupResult.point || pickupPoint;

        const dropoffResult = await validateSharedTransferLocation(
          { hubCity: selectedRoute.dropoff, point: dropoffPoint },
          authTokenRef.current,
        );
        validatedDropoff = dropoffResult.point || dropoffPoint;
      } catch (validationError) {
        setRouteError(validationError.message || 'Pickup or drop-off is outside the service area.');
        setShowErrorFeedback(true);
        setIsSubmitting(false);
        return;
      }

      const { data: participant, error: joinRequestError } = await dbHelpers.joinTripRequest(
        trip.id,
        {
          pickup_location: selectedRoute.pickup,
          dropoff_location: selectedRoute.dropoff,
          contact_name: contactName.trim(),
          contact_phone: contactPhone.trim(),
          pickupPoint: validatedPickup,
          dropoffPoint: validatedDropoff,
          order_notes: orderNotes.trim() || pickupInstructions.trim() || null,
        },
        groupSize,
        `${selectedRoute.pickup}To${selectedRoute.dropoff}`
      );
      if (joinRequestError) {
        const cutoffReason = joinRequestError.reason || extractJoinRejectionReason(joinRequestError);
        setRouteError(
          cutoffReason
            ? joinRejectionMessage(cutoffReason)
            : (joinRequestError.message || 'Error joining trip. Please try again.'),
        );
        setIsSubmitting(false);
        return;
      }

      // Get the participant ID from the result
      const participantId = participant?.[0]?.id;
      if (!participantId) {
        throw new Error('Failed to get participant ID');
      }

      setParticipantId(participantId);
      participantIdRef.current = participantId;
      setPaymentAmount(userCost);

      // Server-side revalidation before opening Stripe (capacity, join cutoffs, duplicate join)
      try {
        const session = (await supabase.auth.getSession()).data.session;
        const precheck = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-payment-handler`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`
          },
          body: JSON.stringify({
            action: 'revalidate_before_payment',
            tripId: trip.id,
            participantId: participantId,
            userId: user.id
          })
        });
        const details = await precheck.json().catch(() => ({}));
        if (!precheck.ok) {
          await supabase.from('participants').delete().eq('id', participantId);
          setIsSubmitting(false);
          setRouteError(joinRejectionMessage(details?.reason) || 'Trip is no longer joinable. Please refresh and try again.');
          return;
        }
        setImmediateCharge(!!details?.immediate_charge);
      } catch (preErr) {
        console.error('Pre-payment revalidation failed:', preErr);
        setIsSubmitting(false);
        setRouteError('Could not validate trip state. Please refresh and try again.');
        return;
      }

      setShowPaymentModal(true);

      // Install best-effort cleanup on abrupt tab close/refresh while payment not completed
      paymentCompletedRef.current = false;
      const beforeUnloadHandler = () => {
        if (!paymentCompletedRef.current && participantIdRef.current) {
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
          const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
          const url = `${supabaseUrl}/rest/v1/participants?id=eq.${participantIdRef.current}`;
          try {
            fetch(url, {
              method: 'DELETE',
              headers: {
                'apikey': anonKey,
                'Authorization': authTokenRef.current ? `Bearer ${authTokenRef.current}` : '',
                'Content-Type': 'application/json'
              },
              keepalive: true
            });
          } catch (_err) {
            // Ignore
          }
        }
      };
      window.addEventListener('beforeunload', beforeUnloadHandler);
      // Store remover on ref for later cleanup
      (window)._joinPaymentBeforeUnloadHandler = beforeUnloadHandler;
      
      // Show success feedback
      setShowSuccessFeedback(true);
      setTimeout(() => setShowSuccessFeedback(false), 2000);
      
    } catch (submitError) {
      setRouteError('Error validating route.');
      setShowErrorFeedback(true);
      setTimeout(() => setShowErrorFeedback(false), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentResult) => {
    try {
      paymentCompletedRef.current = true;
      // Remove abrupt-close handler
      if ((window)._joinPaymentBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', (window)._joinPaymentBeforeUnloadHandler);
        (window)._joinPaymentBeforeUnloadHandler = null;
      }
      // GUARD: Ensure trip is defined
      if (!trip) {
        console.error('Trip object is undefined in payment success handler');
        setShowPaymentModal(false);
        onClose();
        return;
      }
      
      if (!participantId) {
        throw new Error('No participant ID available for update');
      }

      const paymentIntentId = paymentResult?.paymentIntentId;
      if (!paymentIntentId) {
        throw new Error('No payment intent ID returned from Stripe');
      }

      const confirmWithRetry = async (retries = 3, delayMs = 400) => {
        const session = (await supabase.auth.getSession()).data.session;
        if (!session?.access_token) {
          throw new Error('User session not available');
        }
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-payment-handler`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                action: 'confirm_participant_payment',
                tripId: trip.id,
                participantId,
                paymentIntentId,
                orderNotes: orderNotes.trim() || pickupInstructions.trim() || null,
              }),
            });
            const result = await response.json();
            if (response.ok && result.success) return { data: result };
            if (response.ok === false) console.error(`Confirm payment error (attempt ${attempt}):`, result.error);
          } catch (e) {
            console.error(`Confirm payment fetch error (attempt ${attempt}):`, e);
          }
          await new Promise((r) => setTimeout(r, delayMs * attempt));
        }
        return { data: null, error: new Error('confirm_failed') };
      };

      const { data: confirmResult, error: confirmError } = await confirmWithRetry();

      if (confirmError) {
        console.error('Failed to confirm participant payment:', confirmError?.message || confirmError);
        throw new Error(`Failed to confirm participant payment: ${confirmError.message}`);
      }

      if (isManualResolutionConfirmResult(confirmResult)) {
        setShowPaymentModal(false);
        setShowFallbackModal(false);
        try {
          const { toast } = await import('react-hot-toast');
          toast.error(
            manualResolutionMessage(confirmResult.resolution_reason),
            { duration: 10000 },
          );
        } catch (notificationError) {
          console.error('Error showing manual resolution notification:', notificationError);
        }
        setTimeout(async () => {
          onClose();
          try {
            const tripEventEmitter = (await import('../utils/tripEvents')).default;
            const { TRIP_EVENTS } = await import('../utils/tripEvents');
            if (tripEventEmitter && TRIP_EVENTS) {
              tripEventEmitter.emit(TRIP_EVENTS.TRIP_UPDATED, { tripId: trip.id });
            }
          } catch {}
          window.location.href = '/my-trips';
        }, 150);
        return;
      }

      // Fire email notifications (non-blocking)
      (async () => {
        try {
          await sendTripNotification('trip_joined', {
            tripId: trip.id,
            participantId,
            userEmail: user?.email,
          });
          await sendTripNotification('participant_joined_notify_creator', {
            tripId: trip.id,
            participantId,
          });
          await sendTripNotification('participant_joined_notify_participants', {
            tripId: trip.id,
            participantId,
          });
        } catch (e) {
          console.warn('Email notification failed (join):', e);
        }
      })();

      // Check if trip meets minimum after joining
      const updatedSeatsAfterJoin = (trip.seats_filled || 0) + groupSize;
      const meetsMinimumAfterJoin = updatedSeatsAfterJoin >= (trip.min_seat_count || 2);

      // Check if joining will make the trip confirmed
      const willMakeTripConfirmed = meetsMinimumAfterJoin && trip.status === 'open';
      
      if (!meetsMinimumAfterJoin) {
        // Show fallback prompt if minimum isn't met
        setShowPaymentModal(false);
        setShowFallbackModal(true);
        return;
      }
      
      // Show trip confirmed notification if joining makes it confirmed
      if (willMakeTripConfirmed) {
        try {
          const { toast } = await import('react-hot-toast');
          toast.success(`🎉 Trip confirmed! Your joining has guaranteed this trip will take place.`, {
            duration: 6000,
            icon: '✅'
          });
        } catch (notificationError) {
          console.error('Error showing trip confirmed notification:', notificationError);
        }
      }
      
      // Show notification if joining a confirmed trip
      if (trip.status === 'confirmed') {
        try {
          const { toast } = await import('react-hot-toast');
          toast.success(`✅ Joining confirmed trip! This trip is guaranteed to take place.`, {
            duration: 5000,
            icon: '🚗'
          });
        } catch (notificationError) {
          console.error('Error showing confirmed trip notification:', notificationError);
        }
      }

      // Successfully joined - close modal and navigate to My Trips
      setShowPaymentModal(false);
      setShowFallbackModal(false);
      setTimeout(async () => {
        onClose();
        try {
          const tripEventEmitter = (await import('../utils/tripEvents')).default;
          const { TRIP_EVENTS } = await import('../utils/tripEvents');
          if (tripEventEmitter && TRIP_EVENTS) {
            tripEventEmitter.emit(TRIP_EVENTS.TRIP_UPDATED, { tripId: trip.id });
          }
        } catch {}
        window.location.href = '/my-trips';
      }, 150);
      
      // Show success notification with better error handling
      try {
        // Validate trip object before using it
        if (!trip) {
          return;
        }
        
        // Use a simple toast notification instead of the complex notification system
        const { toast } = await import('react-hot-toast');
        toast.success(`Successfully joined trip for ${groupSize} ${groupSize === 1 ? 'person' : 'people'}!`);
        
      } catch (notificationError) {
        console.error('Error showing trip notification:', notificationError?.message || notificationError, { tripId: trip?.id });
        // Don't let notification errors break the success flow
      }
      
      // The "joined" activity entry is written server-side by the
      // log_participant_admission trigger once this rider reaches authorized/paid
      // (20260802150000). It is deliberately not written here: joining inserts the
      // participant as 'pending', so writing at this point logged abandoned
      // checkouts permanently.

      // Emit trip joined event to notify other components with better error handling
      try {
        // Validate trip object before using it
        if (!trip) {
          return;
        }
        
        if (trip.id) {
          const tripEventEmitter = (await import('../utils/tripEvents')).default;
          const { TRIP_EVENTS } = await import('../utils/tripEvents');
          
          // Get user profile for event data
          const { data: { user } } = await supabase.auth.getUser();
          let userName = 'Unknown User';
          let userEmail = user?.email || '';
          
          if (user) {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('name')
                .eq('id', user.id)
                .single();
              userName = profile?.name || userName;
              userEmail = user.email || userEmail;
            } catch (error) {
              console.error('Error loading user profile for event:', error);
            }
          }
          
          // Validate that the event emitter and events are properly imported
          if (tripEventEmitter && TRIP_EVENTS && TRIP_EVENTS.TRIP_JOINED) {
            // Emit with correct data structure for trip updates
            tripEventEmitter.emit(TRIP_EVENTS.TRIP_JOINED, { 
              trip_id: trip.id,
              user_email: userEmail,
              user_name: userName,
              group_size: groupSize || 1
            });
            // Also emit a generic trip update event so listing pages refresh
            tripEventEmitter.emit(TRIP_EVENTS.TRIP_UPDATED, { tripId: trip.id });
          }
        }
      } catch (eventError) {
        console.error('Error emitting trip joined event:', eventError?.message || eventError, { tripId: trip?.id });
        // Don't let event errors break the success flow
      }
      
    } catch (error) {
      console.error('Error updating participant:', error?.message || error, { tripId: trip?.id });
      alert('Payment authorized but failed to update trip. Please contact support.');
    }
  };

  const handlePaymentError = async (error) => {
    console.error('Payment error:', error);
    setShowPaymentModal(false);
    // Remove abrupt-close handler
    if ((window)._joinPaymentBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', (window)._joinPaymentBeforeUnloadHandler);
      (window)._joinPaymentBeforeUnloadHandler = null;
    }
    
    // CRITICAL: Rollback participant creation since payment failed
    if (participantId) {
      try {
        // Delete the participant
        await supabase
          .from('participants')
          .delete()
          .eq('id', participantId);
        
        // Recalculate trip costs after removing participant
        try {
          const { recalculateAllParticipantCosts } = await import('../utils/pricing.js');
          await recalculateAllParticipantCosts(trip.id, dbHelpers);
        } catch (recalcError) {
          console.error('Error recalculating costs after rollback:', recalcError);
        }
        
                 const message = error.message === 'Payment cancelled by user' 
           ? 'Join request cancelled. The participant has been removed from the trip.'
           : `Payment failed: ${error.message}. Join request cancelled. Please try again.`;
         alert(message);
      } catch (rollbackError) {
        console.error('Error during rollback:', rollbackError?.message || rollbackError);
        alert(`Payment failed and unable to cleanup. Please contact support. Participant ID: ${participantId}`);
      }
    } else {
      alert(`Payment failed: ${error.message}`);
    }
    
    // Reset state
    setParticipantId(null);
    setPaymentAmount(0);
    paymentCompletedRef.current = false;
  };

  const handleFallbackAccept = () => {
    setShowFallbackModal(false);
    onClose();
    // Trip will be confirmed with fallback pricing
    try {
      if (trip && trip.pickup_location && trip.dropoff_location) {
        // Check if showFallbackNotification exists before calling it
        if (typeof showFallbackNotification !== 'undefined' && showFallbackNotification?.accepted) {
          showFallbackNotification.accepted({
            pickup_location: trip.pickup_location,
            dropoff_location: trip.dropoff_location
          });
        }
      }
    } catch (error) {
      console.error('Error in fallback accept notification:', error);
    }
  };

  const handleFallbackDecline = async () => {
    setShowFallbackModal(false);
    
    // CRITICAL FIX: Remove the participant if user declines fallback
    if (participantId) {
      try {
        // Remove the participant from the database
        const { error } = await supabase
          .from('participants')
          .delete()
          .eq('id', participantId);
        
        if (error) {
          console.error('Error removing participant:', error?.message || error, { participantId });
        }
      } catch (error) {
        console.error('Error removing participant:', error?.message || error, { participantId });
      }
    }
    
    onClose();
    
    // Trip will be cancelled and refunded
    try {
      if (trip && trip.pickup_location && trip.dropoff_location) {
        // Check if showFallbackNotification exists before calling it
        if (typeof showFallbackNotification !== 'undefined' && showFallbackNotification?.declined) {
          showFallbackNotification.declined({
            pickup_location: trip.pickup_location,
            dropoff_location: trip.dropoff_location
          });
        }
      }
    } catch (error) {
      console.error('Error in fallback decline notification:', error);
    }
  };

  // ── Derived constants for rendering ───────────────────────────────

  const stepLabels = ['Trip Details', 'Review Policies', 'Payment'];

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),1rem)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="bg-white w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-3xl border-0 sm:border sm:border-[#E8DED1] shadow-xl flex flex-col overflow-hidden"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              {/* ── Header ──────────────────────────────────────────── */}
              <div className="px-6 pt-5 pb-4 border-b border-[#E8DED1] flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[#2D1E17] font-serif">Complete Your Booking</h2>
                    <p className="text-sm text-[#7A6E67] mt-0.5">
                      Step {modalStep} of 3 &mdash; {stepLabels[modalStep - 1]}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 -mr-1.5 rounded-xl text-[#7A6E67] hover:bg-stone-100 hover:text-[#2D1E17] transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Step progress bar */}
                <div className="flex items-center gap-1.5 mt-4">
                  {[1, 2, 3].map(step => (
                    <div
                      key={step}
                      className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                        modalStep >= step ? 'bg-[#A44432]' : 'bg-stone-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* ── Scrollable Content ──────────────────────────────── */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <AnimatePresence mode="wait">

                  {/* ════════ STEP 1: Trip Details ════════ */}
                  {modalStep === 1 && (
                    <motion.div
                      key="step1"
                      variants={stepVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      {/* Confirmed trip banner */}
                      {trip.status === 'confirmed' && isConfirmedTripJoinAllowed && (
                        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl">
                          <CheckCircle size={20} className="text-[#2D5A27] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-[#2D5A27]">Trip Confirmed!</p>
                            <p className="text-xs text-green-700 mt-0.5">Minimum met.</p>
                          </div>
                        </div>
                      )}

                      {/* Fallback pending warning */}
                      {isFallbackPending && (
                        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                          <Info size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800">Trip at Risk</p>
                            <p className="text-xs text-amber-700 mt-0.5">
                              Only {trip.seats_filled} of {trip.min_seat_count} riders.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Trip closed warning */}
                      {trip.status === 'confirmed' && !isConfirmedTripJoinAllowed && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                          <Info size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">Trip Closed for Joining</p>
                            <p className="text-xs text-red-700 mt-0.5">
                              {trip.seats_filled >= 6 ? 'Trip is full.' : `Joining closes ${CONFIRMED_JOIN_CUTOFF_HOURS} hours before departure.`}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Route Selection */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-1.5 text-sm font-semibold text-[#2D1E17]">
                          <MapPin size={14} className="text-[#A44432]" />
                          Your Route
                        </label>
                        <select
                          value={selectedRoute?.value || ''}
                          onChange={e => setSelectedRoute(availableRoutes.find(r => r.value === e.target.value) || null)}
                          className="input-field min-h-[44px]"
                          required
                          title="Choose the route segment you want to travel on. Available routes are compatible with existing participants."
                        >
                          <option value="">Select your route</option>
                          {availableRoutes.map(route => (
                            <option key={route.value} value={route.value}>
                              {route.label}
                            </option>
                          ))}
                        </select>

                        {selectedRoute && segmentArrivalTime && segmentDuration && (
                          <div className="flex items-center gap-2 text-xs text-[#7A6E67]">
                            <Clock size={12} />
                            <span>Est. arrival: {segmentArrivalTime}</span>
                            <span className="text-stone-300">&middot;</span>
                            <span>{segmentDuration}</span>
                          </div>
                        )}
                      </div>

                      {/* Route Sharing Display */}
                      {selectedRoute ? (
                        <RouteSharingDisplay
                          selectedRoute={selectedRoute}
                          sharedWith={sharedWith}
                          trip={trip}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-[120px] bg-stone-50 rounded-2xl border border-dashed border-[#E8DED1]">
                          <div className="text-center text-[#7A6E67]">
                            <MapPin size={20} className="mx-auto mb-1.5 opacity-40" />
                            <p className="text-xs">Select a route to see sharing details</p>
                          </div>
                        </div>
                      )}

                      {/* Group Size */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-1.5 text-sm font-semibold text-[#2D1E17]">
                          <Users size={14} className="text-[#A44432]" />
                          Seats Needed
                        </label>
                        <select
                          value={groupSize}
                          onChange={e => setGroupSize(Number(e.target.value))}
                          className="input-field min-h-[44px]"
                          title="Select how many people are in your group. This affects the total cost and seat allocation."
                        >
                          {Array.from({ length: Math.min(4, availableSeats) }, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num} {num === 1 ? 'person' : 'people'}</option>
                          ))}
                        </select>
                      </div>

                      {/* Contact Information */}
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-[#2D1E17]">Contact Information</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-[#7A6E67] mb-1.5">Full Name</label>
                            <input
                              type="text"
                              value={contactName}
                              onChange={(e) => setContactName(e.target.value)}
                              className="input-field min-h-[44px]"
                              placeholder="Your full name"
                              required
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-[#7A6E67] mb-1.5">Phone Number</label>
                            <CustomPhoneInput
                              value={contactPhone}
                              onChange={setContactPhone}
                              onValidationChange={setPhoneValid}
                              placeholder="Best number to reach you"
                              countryClassName="w-fit min-w-[5rem] max-w-[6rem]"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Pickup / drop-off within segment cities */}
                      {selectedRoute && (
                        <div className="space-y-4">
                          <SharedTransferPointPicker
                            hubCity={selectedRoute.pickup}
                            kind="pickup"
                            value={pickupPoint}
                            onChange={setPickupPoint}
                          />
                          <SharedTransferPointPicker
                            hubCity={selectedRoute.dropoff}
                            kind="dropoff"
                            value={dropoffPoint}
                            onChange={setDropoffPoint}
                            showOrderNotes
                            orderNotes={orderNotes}
                            onOrderNotesChange={setOrderNotes}
                          />
                        </div>
                      )}

                      {/* Legacy pickup instructions (hidden; order notes preferred) */}
                      <div className="space-y-1.5 hidden">
                        <label className="block text-xs font-medium text-[#7A6E67]">Pickup Instructions (optional)</label>
                        <textarea
                          value={pickupInstructions}
                          onChange={(e) => setPickupInstructions(e.target.value)}
                          className="input-field min-h-[70px]"
                          placeholder="Landmark, hotel name, special directions"
                          rows={2}
                        />
                      </div>

                      {/* Estimated Cost Panel */}
                      <div className="bg-stone-50 rounded-2xl border border-[#E8DED1] p-4">
                        {selectedRoute ? (
                          isCalculating ? (
                            <div className="flex items-center justify-center gap-2 py-2">
                              <div className="w-4 h-4 border-2 border-[#A44432]/30 border-t-[#A44432] rounded-full animate-spin" />
                              <span className="text-sm text-[#7A6E67]">Calculating cost...</span>
                            </div>
                          ) : userCost > 0 ? (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-[#A44432] font-serif">
                                ${userCost.toFixed(2)}
                              </div>
                              <div className="text-sm text-[#2D1E17] mt-1">
                                ${(userCost / groupSize).toFixed(2)} per person &middot; {groupSize} {groupSize === 1 ? 'seat' : 'seats'}
                              </div>
                              <p className="text-xs text-[#2D5A27] font-medium mt-2">
                                Price drops if more people join
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-[#7A6E67] text-center py-1">Select your route to see pricing</p>
                          )
                        ) : (
                          <p className="text-sm text-[#7A6E67] text-center py-1">Select a route to see pricing</p>
                        )}
                      </div>

                      {/* Error display */}
                      {routeError && (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
                          <p className="text-sm text-red-600">{routeError}</p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* ════════ STEP 2: Policy Review ════════ */}
                  {modalStep === 2 && (
                    <motion.div
                      key="step2"
                      variants={stepVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      {/* Header icon + title */}
                      <div className="flex flex-col items-center text-center pt-2 pb-1">
                        <div className="w-14 h-14 rounded-full bg-[#A44432]/10 flex items-center justify-center mb-3">
                          <ShieldCheck size={28} className="text-[#A44432]" />
                        </div>
                        <h3 className="text-lg font-bold text-[#2D1E17] font-serif">Important Policies</h3>
                        <p className="text-sm text-[#7A6E67] mt-1">Please review before continuing</p>
                      </div>

                      {/* Policy cards */}
                      <div className="space-y-3">
                        {/* Price Drop Guarantee */}
                        <div className="bg-stone-50/50 border border-[#E8DED1] rounded-2xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#A44432] mt-1.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-sm font-semibold text-[#2D1E17]">Price Drop Guarantee</h4>
                              <p className="text-xs text-[#7A6E67] mt-1 leading-relaxed">
                                If more people join after you, the per-person price drops automatically. You&apos;ll always pay only your fair share.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Payment Authorization */}
                        <div className="bg-stone-50/50 border border-[#E8DED1] rounded-2xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#A44432] mt-1.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-sm font-semibold text-[#2D1E17]">Payment Authorization</h4>
                              <p className="text-xs text-[#7A6E67] mt-1 leading-relaxed">
                                {immediateCharge ? (
                                  <>Your card will be charged immediately for <span className="font-semibold text-[#2D1E17]">${userCost.toFixed(2)}</span> — this trip is already confirmed.</>
                                ) : (
                                  <>Your card will be authorized for <span className="font-semibold text-[#2D1E17]">${userCost.toFixed(2)}</span>. We charge it 36 hours before departure once the trip is confirmed.</>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Withdrawal Policy */}
                        <div className="bg-stone-50/50 border border-[#E8DED1] rounded-2xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#A44432] mt-1.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-sm font-semibold text-[#2D1E17]">Withdrawal Policy</h4>
                              <p className="text-xs text-[#7A6E67] mt-1 leading-relaxed">
                                You may withdraw for free before the trip confirms. After confirmation, withdrawals are not allowed.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Booking summary */}
                      <div className="bg-stone-50 rounded-2xl border border-[#E8DED1] p-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#7A6E67]">Route</span>
                          <span className="font-medium text-[#2D1E17]">{selectedRoute?.label}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-2">
                          <span className="text-[#7A6E67]">Group</span>
                          <span className="font-medium text-[#2D1E17]">{groupSize} {groupSize === 1 ? 'person' : 'people'}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-[#E8DED1]">
                          <span className="text-[#7A6E67]">Authorization amount</span>
                          <span className="font-bold text-[#A44432] font-serif">${userCost.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Error display */}
                      {routeError && (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
                          <p className="text-sm text-red-600">{routeError}</p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* ════════ STEP 3: Payment ════════ */}
                  {modalStep === 3 && (
                    <motion.div
                      key="step3"
                      variants={stepVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      {/* Header icon + title */}
                      <div className="flex flex-col items-center text-center pt-2 pb-1">
                        <div className="w-14 h-14 rounded-full bg-[#A44432]/10 flex items-center justify-center mb-3">
                          <CreditCard size={28} className="text-[#A44432]" />
                        </div>
                        <h3 className="text-lg font-bold text-[#2D1E17] font-serif">Billing Details</h3>
                        <p className="text-sm text-[#7A6E67] mt-1">Complete your payment to confirm</p>
                      </div>

                      {/* Billing amount card */}
                      <div className="bg-stone-50 rounded-2xl border border-[#E8DED1] p-5">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-[#7A6E67]">Amount to hold</span>
                          <span className="text-2xl font-bold text-[#A44432] font-serif">${paymentAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-[#7A6E67] border-t border-[#E8DED1] pt-3">
                          <span>{groupSize} {groupSize === 1 ? 'seat' : 'seats'} &middot; {selectedRoute?.label}</span>
                          <Badge type="primary">Authorization Hold</Badge>
                        </div>
                      </div>

                      <div className="flex items-start gap-2 text-xs text-[#7A6E67]">
                        <Info size={14} className="flex-shrink-0 mt-0.5" />
                        <span>Your card will be authorized but not charged. The final amount may be lower if more travelers join.</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── Footer ──────────────────────────────────────────── */}
              <div className="px-6 py-4 border-t border-[#E8DED1] bg-stone-50/50 flex-shrink-0">
                <div className="flex items-center justify-between gap-3">
                  {/* Left button */}
                  {modalStep === 1 ? (
                    <button type="button" onClick={onClose} className="btn-ghost text-sm py-2.5">
                      Cancel
                    </button>
                  ) : modalStep === 2 ? (
                    <button type="button" onClick={handleBack} className="btn-ghost text-sm py-2.5">
                      <ArrowLeft size={16} />
                      Back
                    </button>
                  ) : (
                    <div />
                  )}

                  {/* Right button */}
                  {modalStep === 1 && (
                    <button
                      type="button"
                      onClick={handleStep1Continue}
                      className="btn-primary text-sm"
                      disabled={!selectedRoute || isCalculating || (trip.status === 'confirmed' && !isConfirmedTripJoinAllowed)}
                    >
                      {(trip.status === 'confirmed' && !isConfirmedTripJoinAllowed)
                        ? 'Trip Closed'
                        : <>Continue <ArrowRight size={16} /></>
                      }
                    </button>
                  )}
                  {modalStep === 2 && (
                    <button
                      type="button"
                      onClick={handleStep2Continue}
                      className="btn-primary text-sm"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing…
                        </>
                      ) : (
                        <>Confirm &amp; Pay <ArrowRight size={16} /></>
                      )}
                    </button>
                  )}
                  {modalStep === 3 && (
                    <p className="text-sm text-[#7A6E67] text-center w-full">
                      Complete payment in the secure window
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stripe Payment Modal */}
      <StripePaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          // CRITICAL: Treat modal close as payment cancellation - rollback participant
          handlePaymentError(new Error('Payment cancelled by user'));
        }}
        tripId={trip.id}
        participantId={participantId}
        amount={paymentAmount}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        userProfile={userProfile}
        setUserProfile={setUserProfile}
        user={user}
        immediateCharge={immediateCharge}
      />

      {/* Explainer Modal - Join flow (kept for backward compatibility) */}
      <ExplainerModal
        isOpen={showExplainer}
        onClose={() => setShowExplainer(false)}
        onConfirm={() => {
          setShowExplainer(false);
          handleSubmit({ preventDefault: () => {} });
        }}
        context="join"
        maxAuth={userCost}
        minSeats={trip?.min_seat_count || 2}
        localStorageKey="explainer_seen_join"
      />

      {/* Fallback Prompt Modal */}
      <FallbackPromptModal
        isOpen={showFallbackModal}
        onClose={() => setShowFallbackModal(false)}
        trip={trip}
        participantId={participantId}
        onAccept={handleFallbackAccept}
        onDecline={handleFallbackDecline}
      />

      {/* Success/Error Feedback */}
      <AnimatePresence>
        {showSuccessFeedback && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-2xl shadow-lg z-[60]"
          >
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              <span className="text-sm font-medium">Route validated successfully!</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showErrorFeedback && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl shadow-lg z-[60]"
          >
            <div className="flex items-center gap-2">
              <X size={16} className="text-red-500" />
              <span className="text-sm font-medium">Error validating route. Please try again.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default JoinTripModal;
